import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Linkedin, ShieldCheck, CheckCircle2 } from "lucide-react";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import { COMPANY, SERVICE_OPTIONS, API_URL } from "@/config/company";

const schema = z.object({
  full_name: z.string().trim().min(2, "Enter your full name").max(120),
  work_email: z.string().trim().email("Enter a valid work email address"),
  phone: z.string().trim().max(40, "Phone number is too long").optional().or(z.literal("")),
  company: z.string().trim().min(2, "Enter your company name").max(160),
  job_title: z.string().trim().max(120).optional().or(z.literal("")),
  country: z.string().trim().min(2, "Enter your country").max(80),
  service: z.string().min(1, "Select a service of interest"),
  preferred_contact: z.enum(["Email", "Phone", "Either"], { message: "Select a preferred contact method" }),
  message: z.string().trim().min(10, "Tell us a little more (at least 10 characters)").max(4000),
  consent: z.literal(true, { message: "Your consent is required to submit this form" }),
  website: z.string().max(0).optional().or(z.literal("")),
});

const inputCls =
  "w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm text-ink placeholder:text-muted focus:border-corp focus:outline-none focus:ring-2 focus:ring-bright/30";
const errorCls = "mt-1.5 text-xs text-red-600";

function Field({ id, label, error, children, required = true }) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-semibold text-ink mb-1.5">
        {label} {required && <span className="text-red-600" aria-hidden="true">*</span>}
        {!required && <span className="text-muted font-normal">(optional)</span>}
      </label>
      {children}
      {error && <p id={`${id}-error`} role="alert" className={errorCls}>{error.message}</p>}
    </div>
  );
}

export default function Contact() {
  const [params] = useSearchParams();
  const preselect = SERVICE_OPTIONS.includes(params.get("service")) ? params.get("service") : "";
  const [submitted, setSubmitted] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { service: preselect, preferred_contact: "Email", consent: false },
  });

  const onSubmit = async (data) => {
    try {
      const res = await fetch(`${API_URL}/enquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...data, website: data.website || "" }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.detail || "Submission failed");
      }
      setSubmitted(true);
      toast.success("Your enquiry has been received.");
      reset();
    } catch (err) {
      toast.error(err.message === "Submission failed"
        ? "Your enquiry could not be sent. Please try again or contact us directly."
        : "Your enquiry could not be sent. Please check the form and try again.");
    }
  };

  return (
    <>
      <SEO
        title="Contact"
        description="Request a consultation with Numeric Consulting & Solutions. Tell us about your objectives across ISO standards, cybersecurity, payment security, continuity or technology consulting."
        path="/contact"
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "Contact", path: "/contact" }]}
      />
      <PageHero
        eyebrow="Contact"
        title="Start the Conversation"
        description="Tell us about your organization, your objectives and the challenges you are working through. Our team will review every enquiry personally."
        breadcrumb="Contact"
      />

      <section className="bg-mist py-16 sm:py-24" data-testid="contact-section">
        <div className="container-site grid lg:grid-cols-5 gap-10 items-start">
          {/* Details */}
          <Reveal className="lg:col-span-2 space-y-6">
            <div className="rounded-2xl bg-navy p-8 relative overflow-hidden">
              <div className="absolute inset-0 grid-pattern-dark" aria-hidden="true" />
              <div className="relative">
                <h2 className="font-heading text-xl font-bold text-white">Business Contact Details</h2>
                <ul className="mt-6 space-y-4 text-sm">
                  <li className="flex items-start gap-3 text-slate-300">
                    <Mail size={16} className="mt-0.5 text-teal shrink-0" aria-hidden="true" />
                    <span data-testid="contact-email">{COMPANY.email}</span>
                  </li>
                  <li className="flex items-start gap-3 text-slate-300">
                    <Phone size={16} className="mt-0.5 text-teal shrink-0" aria-hidden="true" />
                    <span data-testid="contact-phone">{COMPANY.phone}</span>
                  </li>
                  <li className="flex items-start gap-3 text-slate-300">
                    <MapPin size={16} className="mt-0.5 text-teal shrink-0" aria-hidden="true" />
                    <span data-testid="contact-address">{COMPANY.address}</span>
                  </li>
                  <li className="flex items-start gap-3 text-slate-300">
                    <Linkedin size={16} className="mt-0.5 text-teal shrink-0" aria-hidden="true" />
                    <a href={COMPANY.linkedin} data-testid="contact-linkedin" className="hover:text-white transition-colors">{COMPANY.linkedin}</a>
                  </li>
                </ul>
                <p className="mt-6 text-xs leading-relaxed text-slate-400 border-t border-white/10 pt-5">
                  Every enquiry is reviewed by our consulting team. We respond as promptly as the nature of
                  your enquiry allows — response times vary with complexity and current engagement load.
                </p>
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-8">
              <h2 className="flex items-center gap-2 font-heading text-base font-bold text-ink">
                <ShieldCheck size={18} className="text-teal-dark" aria-hidden="true" /> Your Privacy
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-muted">
                Information you submit is used only to respond to your enquiry and is handled confidentially.
                It is never sold or shared for marketing.
              </p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="contact-map">
              <iframe
                title="Map showing Ajah, Lagos, Nigeria"
                src="https://www.openstreetmap.org/export/embed.html?bbox=3.505%2C6.415%2C3.665%2C6.520&amp;layer=mapnik&amp;marker=6.4667%2C3.5833"
                className="w-full h-64 border-0"
                loading="lazy"
              />
              <div className="flex items-center justify-between px-5 py-3 text-xs">
                <span className="text-muted">Ajah, Lagos, Nigeria</span>
                <a
                  href="https://www.openstreetmap.org/?mlat=6.4667&amp;mlon=3.5833#map=13/6.4667/3.5833"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="map-larger-link"
                  className="font-semibold text-corp hover:text-bright transition-colors"
                >
                  View larger map
                </a>
              </div>
            </div>
          </Reveal>

          {/* Form */}
          <Reveal delay={0.1} className="lg:col-span-3">
            <div className="rounded-2xl border border-slate-200 bg-white p-7 sm:p-10 shadow-sm">
              {submitted ? (
                <div className="text-center py-10" data-testid="form-success-message" role="status">
                  <CheckCircle2 size={48} className="mx-auto text-teal-dark" aria-hidden="true" />
                  <h2 className="mt-5 font-heading text-2xl font-bold text-navy">Thank You</h2>
                  <p className="mt-3 text-base text-muted max-w-md mx-auto">
                    Your enquiry has been received and will be reviewed by our consulting team. We will be in
                    touch using your preferred contact method.
                  </p>
                  <button
                    type="button"
                    onClick={() => setSubmitted(false)}
                    data-testid="form-submit-another-btn"
                    className="mt-7 rounded-full border border-corp text-corp text-sm font-semibold px-6 py-2.5 hover:bg-corp hover:text-white transition-colors"
                  >
                    Submit Another Enquiry
                  </button>
                </div>
              ) : (
                <>
                  <h2 className="font-heading text-xl sm:text-2xl font-bold text-navy">Request a Consultation</h2>
                  <p className="mt-2 text-sm text-muted">
                    Fields marked <span className="text-red-600" aria-hidden="true">*</span> are required.
                  </p>
                  <form onSubmit={handleSubmit(onSubmit)} noValidate className="mt-8 grid sm:grid-cols-2 gap-5" data-testid="consultation-form">
                    {/* Honeypot */}
                    <div className="hidden" aria-hidden="true">
                      <label htmlFor="website">Website</label>
                      <input id="website" type="text" tabIndex={-1} autoComplete="off" {...register("website")} />
                    </div>

                    <Field id="full_name" label="Full Name" error={errors.full_name}>
                      <input id="full_name" type="text" autoComplete="name" className={inputCls} data-testid="form-full-name"
                        aria-invalid={!!errors.full_name} aria-describedby={errors.full_name ? "full_name-error" : undefined}
                        {...register("full_name")} />
                    </Field>
                    <Field id="work_email" label="Work Email" error={errors.work_email}>
                      <input id="work_email" type="email" autoComplete="email" className={inputCls} data-testid="form-work-email"
                        aria-invalid={!!errors.work_email} aria-describedby={errors.work_email ? "work_email-error" : undefined}
                        {...register("work_email")} />
                    </Field>
                    <Field id="phone" label="Phone Number" error={errors.phone} required={false}>
                      <input id="phone" type="tel" autoComplete="tel" className={inputCls} data-testid="form-phone"
                        aria-invalid={!!errors.phone} aria-describedby={errors.phone ? "phone-error" : undefined}
                        {...register("phone")} />
                    </Field>
                    <Field id="company" label="Company Name" error={errors.company}>
                      <input id="company" type="text" autoComplete="organization" className={inputCls} data-testid="form-company"
                        aria-invalid={!!errors.company} aria-describedby={errors.company ? "company-error" : undefined}
                        {...register("company")} />
                    </Field>
                    <Field id="job_title" label="Job Title" error={errors.job_title} required={false}>
                      <input id="job_title" type="text" autoComplete="organization-title" className={inputCls} data-testid="form-job-title"
                        {...register("job_title")} />
                    </Field>
                    <Field id="country" label="Country" error={errors.country}>
                      <input id="country" type="text" autoComplete="country-name" className={inputCls} data-testid="form-country"
                        aria-invalid={!!errors.country} aria-describedby={errors.country ? "country-error" : undefined}
                        {...register("country")} />
                    </Field>
                    <Field id="service" label="Service of Interest" error={errors.service}>
                      <select id="service" className={inputCls} data-testid="form-service-select"
                        aria-invalid={!!errors.service} aria-describedby={errors.service ? "service-error" : undefined}
                        {...register("service")}>
                        <option value="">Select a service…</option>
                        {SERVICE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </Field>
                    <fieldset>
                      <legend className="text-sm font-semibold text-ink mb-1.5">
                        Preferred Contact Method <span className="text-red-600" aria-hidden="true">*</span>
                      </legend>
                      <div className="flex gap-4 pt-1.5" data-testid="form-preferred-contact">
                        {["Email", "Phone", "Either"].map((m) => (
                          <label key={m} className="inline-flex items-center gap-2 text-sm text-ink cursor-pointer">
                            <input type="radio" value={m} data-testid={`form-contact-${m.toLowerCase()}`}
                              className="accent-[#145DA0] w-4 h-4" {...register("preferred_contact")} />
                            {m}
                          </label>
                        ))}
                      </div>
                      {errors.preferred_contact && <p role="alert" className={errorCls}>{errors.preferred_contact.message}</p>}
                    </fieldset>
                    <div className="sm:col-span-2">
                      <Field id="message" label="Message" error={errors.message}>
                        <textarea id="message" rows={5} className={inputCls} data-testid="form-message"
                          placeholder="Tell us about your objectives, current situation and what you would like to achieve…"
                          aria-invalid={!!errors.message} aria-describedby={errors.message ? "message-error" : undefined}
                          {...register("message")} />
                      </Field>
                    </div>
                    <div className="sm:col-span-2">
                      <label className="flex items-start gap-3 text-sm text-muted cursor-pointer" data-testid="form-consent-label">
                        <input type="checkbox" data-testid="form-consent-checkbox"
                          className="mt-0.5 accent-[#145DA0] w-4 h-4 shrink-0"
                          aria-invalid={!!errors.consent} aria-describedby={errors.consent ? "consent-error" : undefined}
                          {...register("consent")} />
                        <span>
                          I consent to {COMPANY.name} processing the information I have provided for the purpose of
                          responding to my enquiry. <span className="text-red-600" aria-hidden="true">*</span>
                        </span>
                      </label>
                      {errors.consent && <p id="consent-error" role="alert" className={errorCls}>{errors.consent.message}</p>}
                    </div>
                    <div className="sm:col-span-2">
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        data-testid="form-submit-btn"
                        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-teal hover:bg-teal-dark disabled:opacity-60 text-white font-semibold px-8 py-3.5 transition-colors duration-200"
                      >
                        {isSubmitting ? "Sending…" : "Submit Enquiry"}
                      </button>
                    </div>
                  </form>
                </>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
